import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Box, Grid, type GridProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  ButtonWithLoader,
  LanguageUtils,
  type PrimitiveTableRowProps,
  Text,
} from '@pbt/pbt-ui-components'
import { getColumnStyles } from '@pbt/pbt-ui-components/src/utils/primitiveTableUtils'

import type {
  InventoryOrder,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'

import { getOneUomPluralized } from '../../inventoryUtils'
import InventoryOrderItemStatusLabel from '../InventoryOrderItemStatusLabel'
import {
  isReceivingDisabled,
  transformInventoryOrderItemForTable,
} from '../utils'
import { OrderItemFlags } from './OrderItemFlags'

interface StyledRowContainerProps extends GridProps {
  index: number
}

const StyledRowContainer = styled(Grid)<StyledRowContainerProps>`
  border-top: none;
  border-bottom: none;
  background-color: ${({ index, theme }) =>
    index % 2 === 0 ? theme.colors.grayGray4 : 'unset'};
`
const StyledQuantityContainer = styled('div')`
  display: flex;
  flex-direction: column;
`
const StyledQuantityTotal = styled(Text)`
  font-weight: bold;
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  line-height: 1.8rem;
`
const StyledPackageType = styled('span')`
  color: ${({ theme }) => theme.colors.grayGray2};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
`

interface OrderItemTableRowProps extends PrimitiveTableRowProps {
  index: number
  inventoryOrder: InventoryOrder
  item: InventoryOrderItem
}

export const OrderItemTableRow = ({
  item,
  inventoryOrder,
  index,
  data: { mainColumnWidth, otherColumns },
}: OrderItemTableRowProps) => {
  const { t } = useTranslation(['Admin', 'Common'])
  const PackageType = useSelector(getPackageType)
  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const navigate = useNavigate()

  const {
    id,
    name,
    statusId,
    orderedQuantity,
    receivedQuantity,
    damagedQuantity,
    packageTypeId,
    controlled,
    storeCold,
    sku,
  } = transformInventoryOrderItemForTable(item)

  const packageTypeName = LanguageUtils.getConstantTranslatedName(
    packageTypeId,
    PackageType,
  )
  const productSizeUnitName = LanguageUtils.getConstantTranslatedName(
    item.packageUnits.id,
    ProductSizeUnit,
  )
  const { perPackageAmount } = item

  const formatQuantityDisplay = (quantity: number): React.ReactNode => {
    const totalAmount = quantity * perPackageAmount
    const pluralizedProductUnit = getOneUomPluralized(
      productSizeUnitName,
      totalAmount,
    )
    const pluralizedPackageType = getOneUomPluralized(packageTypeName, quantity)

    return (
      <StyledQuantityContainer>
        <StyledQuantityTotal>
          {totalAmount} {pluralizedProductUnit}
        </StyledQuantityTotal>
        <StyledPackageType>
          ({quantity} {pluralizedPackageType})
        </StyledPackageType>
      </StyledQuantityContainer>
    )
  }

  const navigateToItem = () => {
    navigate(`items/${id}`)
  }

  const buttonText = isReceivingDisabled(inventoryOrder.status, item.status)
    ? t('Common:VIEW_ACTION')
    : t('Admin:CATALOG.ORDERS.RECEIVE_ACTION')

  return (
    <StyledRowContainer container alignItems="center" index={index} p={1}>
      <Grid
        item
        alignItems="center"
        display="flex"
        gap={1}
        justifyContent="space-between"
        px={0.5}
        style={getColumnStyles(mainColumnWidth)}
      >
        <Box display="flex" flexDirection="column" gap={1} minWidth={0}>
          <Text variant="body2">{name}</Text>
          <OrderItemFlags controlled={controlled} storeCold={storeCold} />
        </Box>
        <ButtonWithLoader
          fullWidth
          color="secondary"
          size="small"
          sx={{ px: 4, width: '120px' }}
          variant="outlined"
          onClick={navigateToItem}
        >
          {buttonText}
        </ButtonWithLoader>
      </Grid>
      <Grid
        container
        item
        alignItems="center"
        style={getColumnStyles(12 - mainColumnWidth)}
      >
        <Grid
          item
          display="flex"
          px={0.5}
          style={getColumnStyles(otherColumns[0].width)}
        >
          <InventoryOrderItemStatusLabel statusId={statusId} />
        </Grid>
        <Grid item px={0.5} style={getColumnStyles(otherColumns[1].width)}>
          {formatQuantityDisplay(orderedQuantity)}
        </Grid>
        <Grid item px={0.5} style={getColumnStyles(otherColumns[2].width)}>
          {receivedQuantity != null
            ? formatQuantityDisplay(receivedQuantity)
            : '--'}
        </Grid>
        <Grid item px={0.5} style={getColumnStyles(otherColumns[3].width)}>
          {damagedQuantity != null && damagedQuantity > 0
            ? formatQuantityDisplay(damagedQuantity)
            : '--'}
        </Grid>
        <Grid item px={0.5} style={getColumnStyles(otherColumns[4].width)}>
          <Text variant="body2">{sku ?? '-'}</Text>
        </Grid>
      </Grid>
    </StyledRowContainer>
  )
}
